import { useGetDirectusUsersQuery } from '@/features/api/endpoints/userEndpoints';
import type { DirectusUsers } from '@/models/types';
import { getUserLabel } from '@/shared/utils/user.utils';
import { Autocomplete, type AutocompleteProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { IconSearch } from '@tabler/icons-react';
import { useMemo, useState } from 'react';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DELAY_MS = 300;
const MAX_RESULTS = 20;

type Props = Omit<AutocompleteProps, 'data' | 'value' | 'defaultValue' | 'onChange'> & {
  /** Appelé avec l'utilisateur choisi, ou `null` quand la saisie ne correspond plus à un choix. */
  onUserSelect: (user: DirectusUsers | null) => void;
};

const getOptionLabel = (user: DirectusUsers): string => {
  const label = getUserLabel(user, user.email ?? '');
  return user.email && user.email !== label ? `${label} (${user.email})` : label;
};

/**
 * Recherche d'utilisateur par nom ou email. La recherche se fait côté serveur
 * (saisie temporisée, résultats limités) : la liste des utilisateurs n'est jamais chargée en entier.
 */
const UserSearch = ({ onUserSelect, ...autocompleteProps }: Props) => {
  const [search, setSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [debouncedSearch] = useDebouncedValue(search.trim(), SEARCH_DELAY_MS);

  // Pas de requête sous le seuil, ni pour relire le libellé de l'utilisateur déjà choisi.
  const shouldSearch =
    debouncedSearch.length >= MIN_SEARCH_LENGTH && debouncedSearch !== selectedLabel;

  const { data: users = [] } = useGetDirectusUsersQuery(
    shouldSearch
      ? {
          search: debouncedSearch,
          fields: ['id', 'first_name', 'last_name', 'email'],
          sort: ['last_name', 'first_name'],
          limit: MAX_RESULTS,
        }
      : skipToken,
  );

  const usersByLabel = useMemo(
    () => new Map(users.map(user => [getOptionLabel(user), user])),
    [users],
  );

  const handleChange = (value: string) => {
    setSearch(value);
    const user = usersByLabel.get(value) ?? null;
    setSelectedLabel(user ? value : null);
    onUserSelect(user);
  };

  return (
    <Autocomplete
      leftSection={<IconSearch size={16} />}
      // Champ de recherche, pas d'identifiant : on évite les gestionnaires de mots de passe.
      autoComplete="off"
      data-bwignore="true"
      data-lpignore="true"
      data-1p-ignore="true"
      {...autocompleteProps}
      data={[...usersByLabel.keys()]}
      value={search}
      onChange={handleChange}
    />
  );
};

export default UserSearch;
